// Kartlag «Dekning» — hvor en OP-post, radar eller antenne FAKTISK ser/når,
// regnet over EKTE høydedata (Terrarium-DEM via @skade/terrain), ikke en sirkel.
//
// To modi som svarer på HELT forskjellige spørsmål og derfor aldri blandes i UI:
//   · optisk — `sectorViewshed`: hvilke CELLER ser observatøren? Gir flekker og
//     dødt terreng bak kammer. Rendres som rasterflate (synlig + dødt terreng).
//   · rf     — `coverageFootprint`: hvor LANGT rekker signalet per asimut, og HVA
//     som satte grensen (terreng / radiohorisont / tap-budsjett). Rendres som
//     polygon med grense farget etter årsak.
// En viewshed-flekk og en rekkevidde-kant er ikke det samme, og en operatør som
// tror det er blitt lurt.
//
// YTELSE (UX §2/§9 — UI-et skal aldri fryse):
//   · `planleggOppløsning` velger DEM-cellestørrelse, radielt steg og asimut-steg
//     ut fra radius, og sier EKSPLISITT i UI hva som ble valgt. Store områder
//     degraderes i oppløsning — de låser ikke nettleseren.
//   · Sveipen kjøres i asimut-BOLKER med en rammepause mellom hver, så DOM-en får
//     males og fremdriften vises. Ingen web worker (holder koden enkel og
//     avhengighetsfri), men ingen bolk er stor nok til å hakke merkbart.
//
// Filen eier også den DELTE terreng-plumbingen (`hentHoydedata`, formatering,
// datagrunnlag-noten) fordi dette er datalaget. `ui/sikt.ts` importerer HERFRA —
// én retning, ingen import-sykel.
import type { GeoJSONSource, Map as MlMap, MapMouseEvent } from 'maplibre-gl';
import {
  featureCollection, haversineM,
  type ElevationGrid, type GeoFeature, type LatLon,
} from '@skade/contracts';
import {
  TerrariumTileCache, bboxAround, coverageFootprint, gridCoverage, loadElevationGrid,
  sectorSpanDeg, sectorViewshed, tilesForBBox, zoomForResolution,
  type BBox, type CoverageFootprint, type CoverageLimit, type CoverageRay,
  type SectorViewshedResult,
} from '@skade/terrain';

// ── Delt: datagrunnlag, formatering, DEM-lasting ─────────────────────────────

/**
 * Datagrunnlag-noten som BEGGE verktøyene viser. Terrarium er en DTM: en «fri»
 * siktlinje gjennom bebyggelse eller granskog kan være reelt blokkert. Kort, men
 * alltid synlig — jf. VALE §1 (ærlighet) og @skade/terrain sine presisjonsgrenser.
 */
export const DEM_KILDE_NOTE =
  'Grunnlag: Terrarium DTM ~30 m — bakketerreng UTEN bygninger, skog eller master.';

/** Maks antall Terrarium-fliser vi henter for én beregning (≈256 KB dekodet per flis). */
export const MAKS_FLISER = 64;

/** Norsk tallformat (desimalKOMMA) — brukes i all UI-tekst i begge verktøyene. */
export function nf(n: number, desimaler = 1): string {
  return n.toFixed(desimaler).replace('.', ',');
}

/** Avstand: meter under 1 km, ellers km med én desimal. */
export function fmtAvstand(m: number): string {
  if (!Number.isFinite(m)) return '—';
  return Math.abs(m) < 1000 ? `${Math.round(m)} m` : `${nf(m / 1000)} km`;
}

/**
 * Høyde/kote. Hele meter fra 10 m og opp, men én desimal under — antennehøyder
 * og klaringer på 1,7 / 2,5 m ville blitt meningsløse avrundet til 2 m.
 */
export function fmtHoyde(m: number): string {
  if (!Number.isFinite(m)) return '—';
  if (Math.abs(m) < 10 && !Number.isInteger(m)) return `${nf(m)} m`;
  return `${Math.round(m)} m`;
}

/** Vinkel i grader med én desimal. */
export function fmtVinkel(deg: number): string {
  return Number.isFinite(deg) ? `${nf(deg)}°` : '—';
}

/** Areal: m² under 1 km², ellers km². */
export function fmtAreal(m2: number): string {
  if (!Number.isFinite(m2)) return '—';
  return m2 < 1e6 ? `${Math.round(m2 / 1000) * 1000} m²` : `${nf(m2 / 1e6)} km²`;
}

/**
 * DEM-cellestørrelse for en siktlinje av gitt lengde. Lange linjer må grovere
 * ut, ellers sprenger flis-budsjettet (en 50 km linje i 30 m ville krevd ~120
 * fliser). Informasjonsinnholdet i Terrarium er ~30 m uansett, så for korte
 * linjer er 30 m både nok og maksimalt meningsfylt.
 */
export function celleForAvstand(distanceM: number): number {
  if (distanceM <= 15_000) return 30;
  if (distanceM <= 35_000) return 60;
  if (distanceM <= 80_000) return 120;
  return 240;
}

/** Union av flere bokser (til bbox rundt en linje: union av endepunkt-boksene). */
export function unionBoks(bokser: BBox[]): BBox {
  if (!bokser.length) throw new Error('unionBoks: tom liste');
  return bokser.reduce((a, b) => ({
    north: Math.max(a.north, b.north),
    south: Math.min(a.south, b.south),
    west: Math.min(a.west, b.west),
    east: Math.max(a.east, b.east),
  }));
}

/** Boks som dekker alle punktene med `margenM` klaring rundt hvert. */
export function boksRundtPunkter(punkter: LatLon[], margenM: number): BBox {
  if (!punkter.length) throw new Error('boksRundtPunkter: ingen punkt');
  return unionBoks(punkter.map((p) => bboxAround(p, margenM)));
}

export interface Hoydedata {
  grid: ElevationGrid;
  bbox: BBox;
  /** Andel celler med gyldig høyde (0–1). < 1 ⇒ hull, si det i UI. */
  dekning: number;
  celleM: number;
  zoom: number;
  fliser: number;
}

/**
 * Boks → Terrarium-fliser → `ElevationGrid`. Nekter ÆRLIG framfor å henge når
 * området krever flere fliser enn budsjettet.
 *
 * NB: dette er første reelle bruk av nettleser-delen i `@skade/terrain/dem.ts`
 * (fetch + createImageBitmap + OffscreenCanvas). Den er ikke node-testbar, så
 * feil her dukker opp i nettleser — derfor er `dekning` og `fliser` med i
 * resultatet og vises i UI.
 */
export async function hentHoydedataForBoks(
  bbox: BBox,
  celleM: number,
  opts: { cache?: TerrariumTileCache; maksFliser?: number } = {},
): Promise<Hoydedata> {
  const midLat = (bbox.north + bbox.south) / 2;
  const zoom = zoomForResolution(midLat, celleM);
  const fliser = tilesForBBox(bbox, zoom).length;
  const maks = opts.maksFliser ?? MAKS_FLISER;
  if (fliser > maks) {
    throw new Error(
      `Området krever ${fliser} høydefliser (tak ${maks}). Reduser rekkevidde/avstand.`,
    );
  }
  const grid = await loadElevationGrid(bbox, {
    targetMetersPerCell: celleM,
    zoom,
    cache: opts.cache,
  });
  return { grid, bbox, dekning: gridCoverage(grid), celleM, zoom, fliser };
}

/** Sentrum + radius → høydedata (5 % margin så randstrålene ikke faller utenfor). */
export function hentHoydedata(
  senter: LatLon,
  radiusM: number,
  celleM: number,
  opts: { cache?: TerrariumTileCache; maksFliser?: number } = {},
): Promise<Hoydedata> {
  return hentHoydedataForBoks(bboxAround(senter, radiusM * 1.05), celleM, opts);
}

// ── Kartklikk-arbiter (delt med ui/sikt.ts) ──────────────────────────────────
//
// Begge terrengverktøyene har en «vent på kartklikk»-modus. Uten en felles
// arbiter mates ETT klikk til BEGGE: verifisert i nettleser — å plassere
// dekningskilden satte samtidig siktepunkt A, uten at operatøren ba om det.
// Arming er derfor EKSKLUSIV: den som armer sist vinner, og den forrige
// avbrytes. Bevisst minimal (to funksjoner, ingen event-buss) og DOM-fri.

let aktivArming: (() => void) | null = null;

/** Arm eksklusivt: avbryter et annet verktøy som venter på kartklikk. */
export function armEksklusivt(avbryt: () => void): void {
  if (aktivArming && aktivArming !== avbryt) aktivArming();
  aktivArming = avbryt;
}

/** Gi fra seg armingen (kalles fra verktøyets egen avbryt/fullfør). */
export function frigiArming(avbryt: () => void): void {
  if (aktivArming === avbryt) aktivArming = null;
}

/** Venter noe på kartklikk nå? (til test/verifikasjon) */
export function armingAktiv(): boolean {
  return aktivArming !== null;
}

// ── Oppløsningsplan (eksplisitt, ikke gjettet) ───────────────────────────────

export interface Oppløsningsplan {
  /** DEM-cellestørrelse (m på bakken). */
  celleM: number;
  /** Radielt steg i sveipen (m) — halv celle = Nyquist på DEM-et. */
  rangeStegM: number;
  asimutStegDeg: number;
  antallStraler: number;
  /** Antall terreng-oppslag totalt (stråler × steg) — grovt kostnadsmål. */
  punkter: number;
  /** true = grovere enn DEM-ets 30 m for å holde beregningen levelig. */
  degradert: boolean;
  /** Én linje UI-et viser ORDRETT, slik at oppløsningen aldri er skjult. */
  begrunnelse: string;
}

const DEG = 180 / Math.PI;

/**
 * Velg oppløsning ut fra radius:
 *   celle ≈ radius/250 (klemt til 30–250 m), radielt steg = halv celle,
 *   asimut-steg = buen av ÉN celle ved maks rekkevidde.
 *
 * Asimut-steget er bundet til cellestørrelsen med vilje. Et grovere steg (f.eks.
 * et fast 0,5°-gulv) gir stråleavstand > cellestørrelse ytterst, og da får
 * rasterflaten et KAM-MØNSTER av uundersøkte celler mellom strålene — som ser ut
 * som dekningsflekker uten å være det. Vi kan ikke fylle gapene ved å utvide
 * synligheten (det ville PÅSTÅ sikt vi ikke har regnet), så vi sveiper tett nok
 * i stedet. Kostnaden er ~1 mill. terrengoppslag, som bolkevis kjøring tåler.
 */
export function planleggOppløsning(radiusM: number, spanDeg = 360): Oppløsningsplan {
  if (!(radiusM > 0)) throw new Error('planleggOppløsning: radius må være > 0');
  const rå = Math.round(radiusM / 250 / 10) * 10;
  const celleM = Math.min(250, Math.max(30, rå));
  const rangeStegM = celleM / 2;
  const asimutStegDeg = Math.min(2, Math.max(0.02, (celleM / radiusM) * DEG));
  const span = spanDeg <= 0 || spanDeg > 360 ? 360 : spanDeg;
  const antallStraler = Math.max(1, Math.ceil(span / asimutStegDeg));
  const stegPerStrale = Math.max(1, Math.ceil(radiusM / rangeStegM));
  const punkter = antallStraler * stegPerStrale;
  const degradert = celleM > 30;
  return {
    celleM, rangeStegM, asimutStegDeg, antallStraler, punkter, degradert,
    begrunnelse:
      `DEM ${celleM} m · asimut ${nf(asimutStegDeg, 2)}° · ${antallStraler} stråler · ` +
      `${nf(punkter / 1e6, 2)} mill. terrengoppslag` +
      (degradert ? ' · GROVERE enn DEM-ets 30 m for å holde UI-et levende' : ''),
  };
}

// ── Rene omforminger: resultat → kartgeometri ────────────────────────────────

/** OR-fletting av synlighet fra flere asimut-bolker (samme geo-referanse). */
export function slåSammenSynlighet(mål: Uint8Array, kilde: Uint8Array): Uint8Array {
  if (mål.length !== kilde.length) {
    throw new Error(`slåSammenSynlighet: ulik lengde ${mål.length} ≠ ${kilde.length}`);
  }
  for (let i = 0; i < mål.length; i++) if (kilde[i]) mål[i] = 1;
  return mål;
}

export interface GrenseSegment {
  limit: CoverageLimit;
  coords: [number, number][];
}

/**
 * Del dekningsgrensen i sammenhengende strekk med SAMME grenseårsak, slik at
 * kanten kan fargelegges per årsak uten å lage én linje per stråle (720 stk).
 *
 * Hvert segment tar med FØRSTE punkt i neste segment, ellers får kanten glipper.
 * Ved lukket ring (hel sirkel) startes oppdelingen der årsaken SKIFTER, så ingen
 * kjøring blir kunstig delt i to ved indeks 0.
 */
export function segmenterGrense(rays: CoverageRay[], lukket = true): GrenseSegment[] {
  const n = rays.length;
  if (n === 0) return [];
  const pt = (i: number): [number, number] => [rays[i].lon, rays[i].lat];
  if (n === 1) return [{ limit: rays[0].limit, coords: [pt(0)] }];

  let start = 0;
  if (lukket) {
    let skifte = -1;
    for (let i = 0; i < n; i++) {
      if (rays[i].limit !== rays[(i - 1 + n) % n].limit) { skifte = i; break; }
    }
    if (skifte === -1) {
      // Samme årsak hele veien rundt: ett lukket segment.
      const coords = rays.map((_, i) => pt(i));
      coords.push(pt(0));
      return [{ limit: rays[0].limit, coords }];
    }
    start = skifte;
  }

  const segs: GrenseSegment[] = [];
  let cur: GrenseSegment | null = null;
  for (let k = 0; k < n; k++) {
    const i = lukket ? (start + k) % n : k;
    if (!cur || cur.limit !== rays[i].limit) {
      if (cur) cur.coords.push(pt(i)); // knytt forrige segment til dette
      cur = { limit: rays[i].limit, coords: [] };
      segs.push(cur);
    }
    cur.coords.push(pt(i));
  }
  if (lukket && cur) cur.coords.push(pt(start)); // lukk ringen
  return segs;
}

/** Antall stråler per grenseårsak — «hva begrenser dekningen?» i tall. */
export function grenseFordeling(rays: CoverageRay[]): Partial<Record<CoverageLimit, number>> {
  const ut: Partial<Record<CoverageLimit, number>> = {};
  for (const r of rays) ut[r.limit] = (ut[r.limit] ?? 0) + 1;
  return ut;
}

/** Lukket [lon,lat]-ring fra strålene (samme form som `CoverageFootprint.ring`). */
export function ringFraStraler(rays: CoverageRay[]): [number, number][] {
  const ring: [number, number][] = rays.map((r) => [r.lon, r.lat]);
  if (ring.length) ring.push(ring[0]);
  return ring;
}

// Farger. Kart-paint kan ikke bruke CSS-variabler, så tokenverdiene fra
// theme.css er speilet her som konstanter (samme grep som measure.ts/kritiske.ts).
// Ingen bruk av --aff-* (tilhørighetsfargene er reservert militær tilhørighet).
export const FARGE = {
  synlig: [139, 168, 136] as [number, number, number], // --sage
  // --surface-active: en rolig vask som viser HVILKET område som er undersøkt.
  // Må skille seg fra bunnen (--surface #0a0c0a), ellers forsvinner den
  // analyserte flaten helt på det mørke kartet.
  dodt: [31, 36, 31] as [number, number, number],
  rfFyll: '#5b9bd5', // --info
  kilde: '#d4a03a', // --gold
} as const;

/** Kantfarge per grenseårsak. Gull = tap-budsjett (menneskeskapt grense),
 *  salvie = terreng, blå = radiohorisont, grå = ren instrument-rekkevidde,
 *  rød = MANGLENDE data (ikke dekning — ærlig markert). */
export const GRENSE_FARGE: Record<CoverageLimit, string> = {
  terreng: '#8ba888',
  radiohorisont: '#5b9bd5',
  'tap-budsjett': '#d4a03a',
  'maks-rekkevidde': '#8b9b8b',
  'ukjent-terreng': '#c23a3a',
};

export const GRENSE_LABEL: Record<CoverageLimit, string> = {
  terreng: 'Terreng maskerer',
  radiohorisont: 'Radiohorisont',
  'tap-budsjett': 'Tap-budsjett',
  'maks-rekkevidde': 'Instrumentert rekkevidde',
  'ukjent-terreng': 'Mangler høydedata',
};

export interface SynlighetsBilde {
  width: number;
  height: number;
  /** RGBA row-major fra nord — samme rekkefølge som `ImageData`. */
  data: Uint8ClampedArray;
  /** Hjørner (NV, NØ, SØ, SV) for MapLibre `image`-kilden. */
  coordinates: [[number, number], [number, number], [number, number], [number, number]];
  synlige: number;
  undersokt: number;
}

/**
 * Viewshed-rutenett → RGBA-bilde. Rasterflate framfor 100 000 småpolygoner.
 *
 * Tre tilstander, fordi de betyr tre forskjellige ting operativt:
 *   synlig            — observatøren ser cellen
 *   dødt terreng      — innenfor sektor + rekkevidde, men IKKE synlig (mørk vask)
 *   utenfor/uundersøkt — helt gjennomsiktig (ingen påstand)
 *
 * «Innenfor sektor + rekkevidde» regnes analytisk per celle (ekvirektangulær
 * tilnærming av avstand/asimut, god nok på < 100 km) fordi `sectorViewshed` bare
 * returnerer et ANTALL berørte celler, ikke hvilke.
 *
 * Bildets utstrekning er rutenettet utvidet med en halv celle: `ElevationGrid` er
 * NODE-basert (celle [0,0] ligger EKSAKT på (north, west)), mens et bildepiksel
 * dekker et areal.
 */
export function synlighetTilBilde(v: SectorViewshedResult): SynlighetsBilde {
  const { rows, cols, north, west, latStep, lonStep } = v;
  const data = new Uint8ClampedArray(rows * cols * 4);
  const span = sectorSpanDeg(v.fromAzimuthDeg, v.toAzimuthDeg);
  const helSirkel = span >= 360;
  const cosLat = Math.max(0.01, Math.cos((v.observer.lat * Math.PI) / 180));
  const M_PER_DEG = 111_320;
  let synlige = 0;
  let undersokt = 0;

  for (let r = 0; r < rows; r++) {
    const lat = north - r * latStep;
    const dyM = (lat - v.observer.lat) * M_PER_DEG; // nord positiv
    for (let c = 0; c < cols; c++) {
      const lon = west + c * lonStep;
      const dxM = (lon - v.observer.lon) * M_PER_DEG * cosLat; // øst positiv
      const dM = Math.hypot(dxM, dyM);
      const i = r * cols + c;
      const o = i * 4;
      let inne = dM >= v.minRangeM && dM <= v.maxRangeM;
      if (inne && !helSirkel && dM > 0) {
        const az = (Math.atan2(dxM, dyM) * DEG + 360) % 360;
        const rel = (az - v.fromAzimuthDeg + 360) % 360;
        inne = rel <= span;
      }
      if (!inne) continue;
      undersokt++;
      if (v.visible[i] === 1) {
        synlige++;
        data[o] = FARGE.synlig[0]; data[o + 1] = FARGE.synlig[1]; data[o + 2] = FARGE.synlig[2];
        data[o + 3] = 200;
      } else {
        data[o] = FARGE.dodt[0]; data[o + 1] = FARGE.dodt[1]; data[o + 2] = FARGE.dodt[2];
        data[o + 3] = 96;
      }
    }
  }

  const halvLat = latStep / 2;
  const halvLon = lonStep / 2;
  const n = north + halvLat;
  const s = north - (rows - 1) * latStep - halvLat;
  const w = west - halvLon;
  const e = west + (cols - 1) * lonStep + halvLon;
  return {
    width: cols, height: rows, data, synlige, undersokt,
    coordinates: [[w, n], [e, n], [e, s], [w, s]],
  };
}

/**
 * Avstand mellom to nabostråler ved maks rekkevidde (m) = R · Δaz.
 *
 * Denne er en ÆRLIGHETS-grense: er stråleavstanden større enn DEM-cellen, finnes
 * det celler ytterst i flaten som sveipen aldri traff. De vises som «ikke påvist
 * synlig», og det er ikke det samme som «bevist dødt terreng».
 */
export function stralespredningM(radiusM: number, asimutStegDeg: number): number {
  return (radiusM * asimutStegDeg * Math.PI) / 180;
}

/** Areal per DEM-celle (m²) — til «hvor stort område ser OP-posten?». */
export function celleArealM2(g: { latStep: number; lonStep: number }, lat: number): number {
  const M_PER_DEG = 111_320;
  return g.latStep * M_PER_DEG * g.lonStep * M_PER_DEG * Math.cos((lat * Math.PI) / 180);
}

// ── Kartlaget ────────────────────────────────────────────────────────────────

export type DekningModus = 'optisk' | 'rf';

export const DEKNING_MODUS_LABEL: Record<DekningModus, string> = {
  optisk: 'Optisk sikt',
  rf: 'RF-dekning',
};

export type DekningStatus =
  | 'tom' | 'venter-kilde' | 'laster-dem' | 'beregner' | 'ok' | 'ingen-data' | 'feil';

export const DEKNING_STATUS_TEXT: Record<DekningStatus, string> = {
  tom: 'Plasser en kilde (OP-post / antenne) i kartet for å regne dekning.',
  'venter-kilde': 'Klikk i kartet for å plassere kilden.',
  'laster-dem': 'Henter høydedata (Terrarium) …',
  beregner: 'Regner dekning …',
  ok: 'Beregnet over ekte høydedata.',
  'ingen-data': 'Ingen høydedata for punktet — kan ikke svare (ikke det samme som «ingen dekning»).',
  feil: 'Beregningen feilet.',
};

/** Innstillinger operatøren styrer. Rene tall — testbart uten DOM. */
export interface DekningInnstillinger {
  modus: DekningModus;
  antennehoydeM: number;
  malhoydeM: number;
  radiusKm: number;
  minRangeM: number;
  fraAsimutDeg: number;
  tilAsimutDeg: number;
  freqMHz: number;
  effektDbm: number;
  folsomhetDbm: number;
}

export const DEKNING_STANDARD: DekningInnstillinger = {
  modus: 'optisk',
  antennehoydeM: 10,
  malhoydeM: 1.7,
  radiusKm: 8,
  minRangeM: 0,
  fraAsimutDeg: 0,
  tilAsimutDeg: 360,
  freqMHz: 150,
  effektDbm: 40,
  folsomhetDbm: -100,
};

/**
 * Tap-budsjett (dB) utledet av effekt og mottakerfølsomhet. Ingen antenne-
 * gevinst her: den er anleggsspesifikk, og et oppdiktet dBi-tall ville pyntet på
 * rekkevidden. Kabeltap/gevinst legges på av operatøren i effekt-feltet.
 */
export function tapBudsjettDb(effektDbm: number, folsomhetDbm: number): number {
  return effektDbm - folsomhetDbm;
}

/** Sektorbredde i grader (360 når fra === til). */
export function spanAv(innst: DekningInnstillinger): number {
  return sectorSpanDeg(innst.fraAsimutDeg, innst.tilAsimutDeg);
}

const SRC_RF = 'dekning-rf';
const SRC_KILDE = 'dekning-kilde';
const SRC_VS = 'dekning-viewshed';
const LAG_VS = 'dekning-viewshed-lag';
/** Asimut-bolk per rammepause. 30° gir ~60 stråler per bolk — kort nok å ikke hakke. */
const BOLK_DEG = 30;

export interface DekningHooks {
  /** Delt flis-cache (gjenbrukes med siktelinje-verktøyet). */
  cache?: TerrariumTileCache;
  /** Kartsenter — brukes som forslag til kilde. */
  center?: () => { lon: number; lat: number };
  /** Statuslinje i skallet (samme kanal som resten av COP-en). */
  alarm?: (tekst: string) => void;
}

export class DekningLag {
  readonly el: HTMLElement;
  /** Kalles når panelet åpnes/lukkes, så dock-knappen kan speile tilstanden. */
  onStateChange?: () => void;
  /** Kalles når flaten/kilden endrer seg, så objektlista kan tegnes på nytt. */
  onEndring?: () => void;

  private open = false;

  /** Er panelet åpent? Finnes for at verktøylinja skal kunne SI det — dock-merket
   * for dette verktøyet lyste aldri, i motsetning til rutenett og trådkors. */
  get apen(): boolean { return this.open; }
  private ready = false;
  private venterKilde = false;
  private kilde: LatLon | null = null;
  private innst: DekningInnstillinger = { ...DEKNING_STANDARD };
  private status: DekningStatus = 'tom';
  private generasjon = 0;
  private readonly cache: TerrariumTileCache;

  // DOM-referanser vi oppdaterer.
  private statusEl!: HTMLElement;
  private kildeEl!: HTMLElement;
  private planEl!: HTMLElement;
  private resultEl!: HTMLElement;
  private rfFelt!: HTMLElement;
  private modusBtn: Partial<Record<DekningModus, HTMLButtonElement>> = {};
  private beregnBtn!: HTMLButtonElement;

  constructor(private map: MlMap, private hooks: DekningHooks = {}) {
    this.cache = hooks.cache ?? new TerrariumTileCache();
    this.el = this.build();
    // Modus-klassene settes ETTER at `el` finnes (build() kjører før tilordningen).
    this.settModus(this.innst.modus);
    if (map.loaded?.()) this.init();
    else map.on('load', () => this.init());
  }

  // ── Offentlig API (det main.ts wirer) ──────────────────────────────────────

  toggle(): void {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open && !this.kilde) this.armKilde();
    else if (!this.open) this.avbrytArming();
    this.onStateChange?.();
  }

  erApen(): boolean { return this.open; }

  /**
   * Kildepunktet flaten er regnet fra, eller `null` når ingen er satt.
   *
   * Finnes fordi dekningsflaten nå står som et OBJEKT i høyrepanelet. UX-
   * rapportens §6.5: «Et sikteresultat er en påstand om terrenget på et
   * tidspunkt, med inndata. Uten identitet kan ingen se hva påstanden bygget
   * på — og en påstand uten kilde er nettopp det grunnloven forbyr.»
   */
  get kildepunkt(): LatLon | null { return this.kilde; }

  /** Innstillingene flaten ble regnet med (antennehøyder, frekvens, modus). */
  get innstillinger(): Readonly<DekningInnstillinger> { return this.innst; }

  /** Neste kartklikk plasserer kilden. */
  armKilde(): void {
    if (this.venterKilde) return;
    armEksklusivt(this.avbrytArming); // ikke stjel klikket fra siktelinja
    this.venterKilde = true;
    this.setStatus('venter-kilde');
    try {
      this.map.getCanvas().style.cursor = 'crosshair';
      this.map.on('click', this.onKart);
    } catch { /* kart uten canvas (test) */ }
  }

  /** Plasser kilden direkte (kontekstmeny, bokmerke, infrastruktur-objekt). */
  plasser(lon: number, lat: number): void {
    this.avbrytArming();
    this.kilde = { lat, lon };
    this.oppdaterKildeTekst();
    this.tegnKilde();
    if (!this.open) { this.open = true; this.el.classList.add('open'); this.onStateChange?.(); }
    void this.beregn();
  }

  /** Fjern flatene fra kartet (behold innstillingene). */
  tøm(): void {
    this.generasjon++;
    this.fjernViewshed();
    this.settRf([]);
    this.resultEl.replaceChildren();
    this.setStatus(this.kilde ? 'ok' : 'tom');
  }

  // ── Beregning ─────────────────────────────────────────────────────────────

  /**
   * Hent høydedata og regn flaten. Bolkevis med rammepause, så fremdriften
   * VISES og nettleseren ikke låser. Reentrant-sikret med generasjonsteller:
   * en ny beregning gjør den forrige irrelevant, ikke kaotisk.
   */
  async beregn(): Promise<void> {
    if (!this.kilde) { this.setStatus('tom'); return; }
    const gen = ++this.generasjon;
    const senter = this.kilde;
    const radiusM = Math.max(100, this.innst.radiusKm * 1000);
    const span = spanAv(this.innst);
    const plan = planleggOppløsning(radiusM, span);
    this.planEl.textContent = plan.begrunnelse;
    this.beregnBtn.disabled = true;
    this.resultEl.replaceChildren();

    try {
      this.setStatus('laster-dem');
      const hd = await hentHoydedata(senter, radiusM, plan.celleM, { cache: this.cache });
      if (gen !== this.generasjon) return;
      if (hd.dekning === 0) { this.setStatus('ingen-data'); return; }

      this.setStatus('beregner');
      if (this.innst.modus === 'optisk') await this.regnOptisk(gen, hd, radiusM, span, plan);
      else await this.regnRf(gen, hd, radiusM, span, plan);
      if (gen !== this.generasjon) return;
      this.setStatus('ok');
      if (hd.dekning < 0.999) {
        this.leggAdvarsel(
          `${nf((1 - hd.dekning) * 100)} % av rutenettet mangler høydedata (hull i Terrarium) — ` +
          'flaten er ufullstendig der.',
        );
      }
    } catch (e) {
      if (gen !== this.generasjon) return;
      this.setStatus('feil');
      this.leggAdvarsel(e instanceof Error ? e.message : String(e));
    } finally {
      if (gen === this.generasjon) this.beregnBtn.disabled = false;
    }
  }

  private async regnOptisk(
    gen: number, hd: Hoydedata, radiusM: number, span: number, plan: Oppløsningsplan,
  ): Promise<void> {
    const g = hd.grid;
    let akk: Uint8Array | null = null;
    let siste: SectorViewshedResult | null = null;
    let bolker = 0;
    const antall = Math.max(1, Math.ceil(span / BOLK_DEG));

    for (let b = 0; b < antall; b++) {
      const fra = this.innst.fraAsimutDeg + (span * b) / antall;
      const til = this.innst.fraAsimutDeg + (span * (b + 1)) / antall;
      const del = sectorViewshed(g, this.kilde as LatLon, {
        observerHeightM: this.innst.antennehoydeM,
        targetHeightM: this.innst.malhoydeM,
        maxRangeM: radiusM,
        minRangeM: this.innst.minRangeM,
        fromAzimuthDeg: fra,
        toAzimuthDeg: til,
        azimuthStepDeg: plan.asimutStegDeg,
        rangeStepM: plan.rangeStegM,
      });
      if (!del) { this.setStatus('ingen-data'); return; }
      akk = akk ? slåSammenSynlighet(akk, del.visible) : new Uint8Array(del.visible);
      siste = del;
      bolker++;
      this.statusEl.textContent =
        `Regner dekning … ${Math.round((bolker / antall) * 100)} %`;
      await nesteRamme();
      if (gen !== this.generasjon) return;
    }
    if (!akk || !siste) return;

    // Sett hele sektoren tilbake på resultatet før rendering (bolkene kjente bare
    // sin egen vifte).
    const samlet: SectorViewshedResult = {
      ...siste, visible: akk,
      fromAzimuthDeg: ((this.innst.fraAsimutDeg % 360) + 360) % 360,
      toAzimuthDeg: ((this.innst.tilAsimutDeg % 360) + 360) % 360,
      minRangeM: this.innst.minRangeM, maxRangeM: radiusM,
    };
    const bilde = synlighetTilBilde(samlet);
    this.settViewshed(bilde);
    this.settRf([]); // modiene skal aldri vises samtidig

    const areal = celleArealM2(g, (this.kilde as LatLon).lat);
    const spredning = stralespredningM(radiusM, plan.asimutStegDeg);
    this.visResultat([
      ['Synlig areal', fmtAreal(bilde.synlige * areal)],
      ['Ikke påvist synlig', fmtAreal(Math.max(0, bilde.undersokt - bilde.synlige) * areal)],
      ['Synlig andel', bilde.undersokt ? `${nf((bilde.synlige / bilde.undersokt) * 100)} %` : '—'],
      ['Antennehøyde', `${fmtHoyde(this.innst.antennehoydeM)} over bakken`],
      ['Målhøyde', `${fmtHoyde(this.innst.malhoydeM)} over bakken`],
      ['Kildens kote', fmtHoyde(samlet.observerGroundElevM)],
      ['Rekkevidde', fmtAvstand(radiusM)],
      ['Sektor', span >= 360 ? 'Hele horisonten' : `${Math.round(this.innst.fraAsimutDeg)}° → ${Math.round(this.innst.tilAsimutDeg)}° (${Math.round(span)}°)`],
      ['Sveipoppløsning', `celle ${fmtHoyde(plan.celleM)} · stråleavstand ${fmtAvstand(spredning)} ytterst`],
    ]);
    // Står strålene lenger fra hverandre enn cellene, finnes det celler ytterst
    // sveipen ALDRI traff. De males som «ikke påvist synlig» — det er ikke det
    // samme som bevist dødt terreng, og det skal stå her.
    if (spredning > plan.celleM * 1.2) {
      this.leggAdvarsel(
        `Strålene står ${fmtAvstand(spredning)} fra hverandre ytterst, mot ${fmtHoyde(plan.celleM)} celler: ` +
        'i ytterkanten kan «ikke påvist synlig» være celler sveipen ikke traff. ' +
        'Kortere rekkevidde gir tettere sveip.',
      );
    }
  }

  private async regnRf(
    gen: number, hd: Hoydedata, radiusM: number, span: number, plan: Oppløsningsplan,
  ): Promise<void> {
    const g = hd.grid;
    const rays: CoverageRay[] = [];
    let forste: CoverageFootprint | null = null;
    const antall = Math.max(1, Math.ceil(span / BOLK_DEG));
    const maxPathLossDb = tapBudsjettDb(this.innst.effektDbm, this.innst.folsomhetDbm);

    for (let b = 0; b < antall; b++) {
      const fra = this.innst.fraAsimutDeg + (span * b) / antall;
      const til = this.innst.fraAsimutDeg + (span * (b + 1)) / antall;
      const del = coverageFootprint(g, this.kilde as LatLon, {
        freqMHz: this.innst.freqMHz,
        antennaHeightM: this.innst.antennehoydeM,
        targetHeightM: this.innst.malhoydeM,
        maxRangeM: radiusM,
        minRangeM: this.innst.minRangeM,
        fromAzimuthDeg: fra,
        toAzimuthDeg: til,
        azimuthStepDeg: plan.asimutStegDeg,
        rangeStepM: plan.rangeStegM,
        maxPathLossDb,
      });
      if (!del) { this.setStatus('ingen-data'); return; }
      forste ??= del;
      rays.push(...del.rays);
      this.statusEl.textContent = `Regner dekning … ${Math.round(((b + 1) / antall) * 100)} %`;
      await nesteRamme();
      if (gen !== this.generasjon) return;
    }
    if (!forste || !rays.length) return;

    this.fjernViewshed(); // modiene skal aldri vises samtidig
    const ring = ringFraStraler(rays);
    const segs = segmenterGrense(rays, span >= 360);
    this.settRf([
      { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { rolle: 'flate' } },
      ...segs.map<GeoFeature>((s) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: s.coords },
        properties: { rolle: 'kant', limit: s.limit },
      })),
    ]);

    const rekkevidder = rays.map((r) => r.rangeM).sort((a, b) => a - b);
    const median = rekkevidder[Math.floor(rekkevidder.length / 2)];
    const fordeling = grenseFordeling(rays);
    this.visResultat([
      ['Median rekkevidde', fmtAvstand(median)],
      ['Minste / største', `${fmtAvstand(rekkevidder[0])} / ${fmtAvstand(rekkevidder[rekkevidder.length - 1])}`],
      ['Frekvens', `${nf(this.innst.freqMHz, 0)} MHz`],
      ['Tap-budsjett', `${nf(maxPathLossDb, 0)} dB (${nf(this.innst.effektDbm, 0)} dBm − ${nf(this.innst.folsomhetDbm, 0)} dBm)`],
      ['Antenne / mål', `${fmtHoyde(this.innst.antennehoydeM)} / ${fmtHoyde(this.innst.malhoydeM)} over bakken`],
      ['Kildens kote', fmtHoyde(forste.siteGroundElevM)],
      ...(Object.keys(fordeling) as CoverageLimit[]).map<[string, string]>((l) => [
        GRENSE_LABEL[l], `${Math.round(((fordeling[l] ?? 0) / rays.length) * 100)} % av asimutene`,
      ]),
    ]);
    this.leggAdvarsel(
      'RF: enkelt knivkant-diffraksjon (Deygout hovedhinder). Tapet UNDERVURDERES ' +
      'med flere åsrygger på rad. Ingen radarligning — dette er sikt/tap, ikke ekkostyrke.',
    );
  }

  // ── Kartlag ───────────────────────────────────────────────────────────────

  private init(): void {
    if (this.ready) return;
    try {
      if (!this.map.getSource(SRC_RF)) {
        this.map.addSource(SRC_RF, { type: 'geojson', data: featureCollection([]) as never });
        const før = this.førsteDatalag();
        this.map.addLayer({
          id: 'dekning-rf-fyll', type: 'fill', source: SRC_RF,
          filter: ['==', ['get', 'rolle'], 'flate'],
          paint: { 'fill-color': FARGE.rfFyll, 'fill-opacity': 0.18 },
        }, før);
        this.map.addLayer({
          id: 'dekning-rf-kant', type: 'line', source: SRC_RF,
          filter: ['==', ['get', 'rolle'], 'kant'],
          paint: {
            'line-width': 2.4,
            'line-color': [
              'match', ['get', 'limit'],
              'terreng', GRENSE_FARGE.terreng,
              'radiohorisont', GRENSE_FARGE.radiohorisont,
              'tap-budsjett', GRENSE_FARGE['tap-budsjett'],
              'ukjent-terreng', GRENSE_FARGE['ukjent-terreng'],
              GRENSE_FARGE['maks-rekkevidde'],
            ],
          },
        }, før);
      }
      if (!this.map.getSource(SRC_KILDE)) {
        this.map.addSource(SRC_KILDE, { type: 'geojson', data: featureCollection([]) as never });
        this.map.addLayer({
          id: 'dekning-kilde-lag', type: 'circle', source: SRC_KILDE,
          paint: {
            'circle-radius': 6, 'circle-color': FARGE.kilde,
            'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 2,
          },
        });
      }
      this.ready = true;
    } catch { /* kart uten lag-API (test) — panelet virker fortsatt */ }
  }

  /** Legg flatene UNDER symboler/spor, ellers dekker de situasjonsbildet. */
  private førsteDatalag(): string | undefined {
    for (const id of ['history', 'shared-fill', 'cep-fill', 'lines', 'symbols']) {
      try { if (this.map.getLayer(id)) return id; } catch { /* ignorer */ }
    }
    return undefined;
  }

  private settRf(feats: GeoFeature[]): void {
    this.init();
    try {
      (this.map.getSource(SRC_RF) as GeoJSONSource | undefined)
        ?.setData(featureCollection(feats) as never);
    } catch { /* ingen kilde (test) */ }
  }

  private tegnKilde(): void {
    this.init();
    const f: GeoFeature[] = this.kilde
      ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: [this.kilde.lon, this.kilde.lat] }, properties: {} }]
      : [];
    try {
      (this.map.getSource(SRC_KILDE) as GeoJSONSource | undefined)
        ?.setData(featureCollection(f) as never);
    } catch { /* ingen kilde (test) */ }
  }

  private settViewshed(bilde: SynlighetsBilde): void {
    this.fjernViewshed();
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bilde.width;
      canvas.height = bilde.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // createImageData + set framfor `new ImageData(...)`: samme resultat, men
      // uten å binde oss til hvilken ArrayBuffer-variant lib.dom krever.
      const img = ctx.createImageData(bilde.width, bilde.height);
      img.data.set(bilde.data);
      ctx.putImageData(img, 0, 0);
      this.map.addSource(SRC_VS, {
        type: 'image', url: canvas.toDataURL('image/png'), coordinates: bilde.coordinates,
      } as never);
      this.map.addLayer({
        id: LAG_VS, type: 'raster', source: SRC_VS,
        paint: { 'raster-opacity': 0.75, 'raster-resampling': 'nearest' },
      }, this.førsteDatalag());
    } catch { /* ingen canvas/kart (test) */ }
  }

  private fjernViewshed(): void {
    try { if (this.map.getLayer(LAG_VS)) this.map.removeLayer(LAG_VS); } catch { /* ignorer */ }
    try { if (this.map.getSource(SRC_VS)) this.map.removeSource(SRC_VS); } catch { /* ignorer */ }
  }

  private onKart = (e: MapMouseEvent) => {
    if (!this.venterKilde) return;
    this.plasser(e.lngLat.lng, e.lngLat.lat);
  };

  /** Arrow-egenskap: arbiteren trenger STABIL identitet på avbryt-funksjonen. */
  private avbrytArming = (): void => {
    frigiArming(this.avbrytArming);
    if (!this.venterKilde) return;
    this.venterKilde = false;
    try {
      this.map.getCanvas().style.cursor = '';
      this.map.off('click', this.onKart);
    } catch { /* ignorer */ }
  };

  // ── Panel-DOM (docked, samme mal som ui/weather.ts) ────────────────────────

  private build(): HTMLElement {
    const p = div('report-composer dekning-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Dekning — OP / radar / antenne'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.type = 'button'; x.title = 'Lukk';
    x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    // Modus: to spørsmål, aldri blandet.
    const modus = div('report-types');
    for (const m of ['optisk', 'rf'] as DekningModus[]) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'report-type-btn' + (m === this.innst.modus ? ' active' : '');
      b.textContent = DEKNING_MODUS_LABEL[m];
      b.title = m === 'optisk'
        ? 'Hvilke celler SER observatøren (viewshed + dødt terreng)'
        : 'Hvor LANGT rekker signalet per asimut, og hva som satte grensen';
      b.onclick = () => this.settModus(m);
      this.modusBtn[m] = b;
      modus.append(b);
    }
    p.append(modus);

    this.kildeEl = div('dek-kilde');
    p.append(this.kildeEl);
    const velg = document.createElement('button');
    velg.type = 'button'; velg.className = 'new-report-btn';
    velg.textContent = 'Plasser kilde i kartet';
    velg.onclick = () => this.armKilde();
    p.append(velg);

    const i = this.innst;
    const form = div('report-form');
    form.append(
      this.tallFelt('Antennehøyde over bakken (m)', () => i.antennehoydeM, (v) => { i.antennehoydeM = v; }, 0, 300, 0.5, [
        ['Øyehøyde 1,7 m', 1.7], ['Kjøretøytak 2,5 m', 2.5], ['Tak 4. etasje 12 m', 12],
        ['Mast 15 m', 15], ['Mast 30 m', 30], ['Mast 60 m', 60],
      ]),
      this.tallFelt('Målhøyde over bakken (m)', () => i.malhoydeM, (v) => { i.malhoydeM = v; }, 0, 5000, 0.5, [
        ['Bakkenivå 0 m', 0], ['Person 1,7 m', 1.7], ['Kjøretøy 2,5 m', 2.5],
        ['Drone 100 m', 100], ['Helikopter 300 m', 300],
      ]),
      this.tallFelt('Rekkevidde (km)', () => i.radiusKm, (v) => { i.radiusKm = v; }, 0.5, 120, 0.5),
      this.tallFelt('Nærgrense (m)', () => i.minRangeM, (v) => { i.minRangeM = v; }, 0, 20000, 50),
      this.tallFelt('Sektor fra (°)', () => i.fraAsimutDeg, (v) => { i.fraAsimutDeg = v; }, 0, 360, 1),
      this.tallFelt('Sektor til (°)', () => i.tilAsimutDeg, (v) => { i.tilAsimutDeg = v; }, 0, 360, 1),
    );
    this.rfFelt = div('dek-rf-felt');
    this.rfFelt.append(
      this.tallFelt('Frekvens (MHz)', () => i.freqMHz, (v) => { i.freqMHz = v; }, 1, 40000, 1, [
        ['VHF samband 150 MHz', 150], ['UHF 450 MHz', 450], ['Drone-link 2400 MHz', 2400],
        ['Radar X-bånd 9400 MHz', 9400],
      ]),
      this.tallFelt('Sendereffekt (dBm)', () => i.effektDbm, (v) => { i.effektDbm = v; }, -30, 90, 1),
      this.tallFelt('Mottakerfølsomhet (dBm)', () => i.folsomhetDbm, (v) => { i.folsomhetDbm = v; }, -140, 0, 1),
    );
    form.append(this.rfFelt);
    p.append(form);

    this.beregnBtn = document.createElement('button');
    this.beregnBtn.type = 'button';
    this.beregnBtn.className = 'report-submit';
    this.beregnBtn.textContent = 'Beregn dekning';
    this.beregnBtn.onclick = () => void this.beregn();
    p.append(this.beregnBtn);

    this.statusEl = div('dek-status');
    this.planEl = div('dek-plan');
    this.resultEl = div('dek-result');
    p.append(this.statusEl, this.planEl, this.resultEl, this.tegnforklaring());

    const note = div('dek-note');
    note.textContent = DEM_KILDE_NOTE;
    p.append(note);

    this.oppdaterKildeTekst();
    this.setStatus('tom');
    return p;
  }

  private tegnforklaring(): HTMLElement {
    const wrap = div('dek-legend');
    const optisk = div('dek-legend-grp dek-legend-optisk');
    optisk.append(
      legendRad('dek-sw-synlig', 'Synlig fra kilden'),
      legendRad('dek-sw-dodt', 'Ikke påvist synlig (dødt terreng / mellom stråler)'),
    );
    const rf = div('dek-legend-grp dek-legend-rf');
    for (const l of Object.keys(GRENSE_LABEL) as CoverageLimit[]) {
      const rad = div('dek-legend-rad');
      const sw = div('dek-sw');
      sw.style.background = GRENSE_FARGE[l];
      rad.append(sw, span('dek-legend-t', GRENSE_LABEL[l]));
      rf.append(rad);
    }
    wrap.append(optisk, rf);
    return wrap;
  }

  /** Tallfelt + valgfri forhåndsvalg-liste (skriver rett inn i tallfeltet). */
  private tallFelt(
    etikett: string, les: () => number, skriv: (v: number) => void,
    min: number, max: number, steg: number,
    forvalg?: [string, number][],
  ): HTMLElement {
    const f = div('report-field');
    f.append(span('report-field-label', etikett));
    const rad = div('dek-felt-rad');
    const inp = document.createElement('input');
    inp.type = 'number'; inp.className = 'report-input dek-num';
    inp.min = String(min); inp.max = String(max); inp.step = String(steg);
    inp.value = String(les());
    const bruk = (v: number) => skriv(Math.min(max, Math.max(min, v)));
    inp.onchange = () => { const v = Number(inp.value); if (Number.isFinite(v)) bruk(v); };
    rad.append(inp);
    if (forvalg) {
      const sel = document.createElement('select');
      sel.className = 'report-input dek-forvalg';
      sel.title = 'Forhåndsvalg';
      const tom = document.createElement('option');
      tom.value = ''; tom.textContent = 'Forvalg …';
      sel.append(tom);
      for (const [navn, verdi] of forvalg) {
        const o = document.createElement('option');
        o.value = String(verdi); o.textContent = navn;
        sel.append(o);
      }
      sel.onchange = () => {
        if (sel.value === '') return;
        const v = Number(sel.value);
        inp.value = String(v);
        sel.value = '';
        bruk(v);
      };
      rad.append(sel);
    }
    f.append(rad);
    return f;
  }

  private settModus(m: DekningModus): void {
    this.innst.modus = m;
    for (const k of ['optisk', 'rf'] as DekningModus[]) {
      this.modusBtn[k]?.classList.toggle('active', k === m);
    }
    this.rfFelt.classList.toggle('vis', m === 'rf');
    this.el.classList.toggle('modus-rf', m === 'rf');
    this.el.classList.toggle('modus-optisk', m === 'optisk');
  }

  private setStatus(s: DekningStatus): void {
    this.status = s;
    this.statusEl.textContent = DEKNING_STATUS_TEXT[s];
    this.statusEl.className = `dek-status dek-${s}`;
    if (s === 'ingen-data' || s === 'feil') this.hooks.alarm?.(DEKNING_STATUS_TEXT[s]);
    // ÉN melding om at dekningsflaten er endret — se samme grep i `ui/sikt.ts`.
    this.onEndring?.();
  }

  /** Gjeldende status — til test/verifikasjon uten å lese DOM. */
  statusNå(): DekningStatus { return this.status; }

  private oppdaterKildeTekst(): void {
    this.kildeEl.replaceChildren();
    if (!this.kilde) {
      this.kildeEl.append(span('dek-kilde-tom', 'Ingen kilde valgt'));
      return;
    }
    this.kildeEl.append(span(
      'dek-kilde-pos',
      `${nf(this.kilde.lat, 5)}, ${nf(this.kilde.lon, 5)}`,
    ));
  }

  private visResultat(rader: [string, string][]): void {
    this.resultEl.replaceChildren();
    for (const [k, v] of rader) {
      const r = div('report-kv');
      r.append(span('report-k', k), span('report-v', v));
      this.resultEl.append(r);
    }
  }

  private leggAdvarsel(tekst: string): void {
    const a = div('dek-advarsel');
    a.textContent = tekst;
    this.resultEl.append(a);
  }
}

/** Slipp kontrollen til nettleseren så DOM males mellom bolkene. */
export function nesteRamme(): Promise<void> {
  return new Promise((res) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => res());
    else setTimeout(res, 0);
  });
}

/** Avstand mellom to punkt (m) — reeksportert for `ui/sikt.ts`. */
export function avstandM(a: LatLon, b: LatLon): number {
  return haversineM(a, b);
}

function div(cls: string): HTMLElement {
  const e = document.createElement('div');
  e.className = cls;
  return e;
}
function span(cls: string, tekst: string): HTMLElement {
  const e = document.createElement('span');
  e.className = cls;
  e.textContent = tekst;
  return e;
}
function legendRad(swCls: string, tekst: string): HTMLElement {
  const rad = div('dek-legend-rad');
  const sw = div(`dek-sw ${swCls}`);
  rad.append(sw, span('dek-legend-t', tekst));
  return rad;
}
